/* Copyright (c) 2020-present CloudIO Inc */

import {
  Attribute,
  FieldType,
  FieldTypeArray,
  IoDatasource,
} from '@cloudio-saas/datasource-types';
import { setIn } from '@cloudio-saas/mutx';
import { getIoDatasourceStoreProps } from 'store/utils/utils';
import {
  emptyFunction,
  useEvent,
  useStateXAction,
  useStateXAtomSetter,
  useStateXRemover,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import NoDataFound from 'api/NoDataFound';
import { FieldTypeIcons } from 'common/DatasourceAttributeTypeIcons';
import { Item, Page } from 'componentregistry';
import { getUrl } from 'config/config';
import { pageAtom } from 'designer/atoms';
import { autoPopulateChildrenAction } from 'designer/state';
import Icon, { parseIconText } from 'icons/Icon';
import ExpandCollapseIcon from 'layout/Sidebar/panels/ExpandCollapseIcon';
import { camelCase, snakeCase } from 'lodash-es';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useEffect, useMemo, useState } from 'react';
import useComponentSize from 'hooks/useComponentSize';
import PageContainer from 'root/PageContainer';
import useUser, {
  useUserCSVDateFormat,
  useUserCSVDateTimeFormat,
} from 'root/useUser';
import DSAttributesCache, {
  attributesPath,
  pkAttributesPath,
} from 'store/DSAttributesCache';
import useDataStore from 'store/useDataStore';
import WaveDots from 'utils/WaveDots';
import { getURLQueryStringToPost } from 'utils/url';
import { StaticRuntimeContext } from 'common/Context';
import {
  DSView,
  sidebarActiveDatasourceIdAtom,
  sidebarDataSourceWizardTitleAtom,
} from '../../../atoms';
import type {
  Column,
  SuccessResult,
  AcceptFileType,
} from './DataSourceWizardTypes';
import useSiblingComponentSize from 'hooks/useSiblingComponentSize';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    gap: '16px',
    backgroundColor: theme.palette.background.default,
  },
  previewTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  preview: {
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  fields: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  fieldsList: {
    flex: 1,
    maxHeight: 120,
    overflow: 'auto',
    '& > div': {
      marginRight: 8,
      marginBottom: 8,
    },
  },
  columnEdit: {
    flex: 0,
    minWidth: '200px',
    display: 'flex',
    '& > div': {
      marginBottom: 8,
      marginRight: 8,
    },
  },
  form: {
    flexBasis: 'auto',
    flex: 0,
    '& > *': {
      marginTop: 20,
    },
  },
}));

const canvasGrid: Item<'CanvasGrid'> = {
  children: ['rowNumberColumn'],
  itemId: 'canvasGrid',
  parent: 'verticalSplitPanel',
  props: {
    enableFormulaColumn: false,
    showBorder: false,
    updatable: {
      type: 'boolean',
      value: true,
    },
  },
  type: 'CanvasGrid',
};

const getPage = (ds: string): Page => {
  return {
    canvasGrid,
    dataStore: {
      children: [],
      itemId: 'dataStore',
      parent: 'root',
      props: {
        alias: 'alias',
        autoQuery: false,
        dataSource: ds,
        deletable: false,
        filterLocally: true,
        insertable: {
          type: 'boolean',
          value: false,
        },
        updatable: {
          type: 'boolean',
          value: true,
        },
        queryable: false,
      },
      type: 'DataStore',
    },
    dataStoreReference: {
      children: ['verticalSplitPanel'],
      itemId: 'dataStoreReference',
      parent: 'flexItem',
      props: {
        alias: 'alias',
      },
      type: 'DataStoreReference',
    },
    flexItem: {
      children: ['dataStoreReference'],
      itemId: 'flexItem',
      parent: 'flexPanel',
      props: {},
      type: 'FlexItem',
    },
    flexPanel: {
      children: ['flexItem'],
      itemId: 'flexPanel',
      parent: 'root',
      props: {},
      type: 'FlexPanel',
    },
    form: {
      children: [],
      itemId: 'form',
      parent: 'verticalSplitPanel',
      props: {},
      type: 'Form',
    },
    root: {
      children: ['dataStore', 'flexPanel'],
      itemId: 'root',
      parent: 'root',
      props: {
        border: {
          all: {
            style: 'solid',
            width: 0,
          },
        },
        padding: {
          all: 0,
        },
        windowTitle: {
          type: 'text',
          value: 'Blank Page',
        },
      },
      type: 'Page',
    },
    verticalSplitPanel: {
      children: ['canvasGrid', 'form'],
      itemId: 'verticalSplitPanel',
      parent: 'dataStoreReference',
      props: {
        fixedSide: 'end',
        fixedWidth: 400,
        fixedWidthMax: 1200,
        fixedWidthMin: 0,
      },
      type: 'VerticalSplitPanel',
    },
    rowNumberColumn: {
      children: [],
      itemId: 'rowNumberColumn',
      parent: 'canvasGrid',
      props: {
        label: '#',
        width: 60,
      },
      type: 'RowNumberColumn',
    },
  } as Page;
};

let counter = 0;

function DataSourceWizardReview({
  setView,
  appUid,
  roleUid,
  result,
  accept,
  back,
  width,
}: {
  appUid: string;
  roleUid: string;
  setView: (_view: DSView) => void;
  result: SuccessResult;
  accept: AcceptFileType;
  back: () => void;
  width: number;
}): React.ReactElement | null {
  const classes = useStyles();
  const user = useUser();
  const [columnIndex, setColumnIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const setTitle = useStateXAtomSetter(sidebarDataSourceWizardTitleAtom);
  const [pageId] = useState(() => `__ds__${++counter}`);
  const ds = pageId;
  const listStore = useDataStore<IoDatasource>(
    getIoDatasourceStoreProps(appUid),
  );
  const setEditId = useStateXAtomSetter(sidebarActiveDatasourceIdAtom);
  const set = useStateXSetter();
  const remove = useStateXRemover();
  const platform = usePlatform();
  const autoPopulateChildrenActionFn = useStateXAction(
    autoPopulateChildrenAction,
  );
  const df = useUserCSVDateFormat();
  const dtf = useUserCSVDateTimeFormat();

  const store = useDataStore({
    appUid,
    pageId,
    ds,
    alias: 'alias',
    filterLocally: true,
  });
  const { columns: _columns, sampleRows } = result;
  const [columns, setColumns] = useState<Record<number, Column>>(_columns);
  const indexes = useMemo(
    () =>
      Object.keys(columns)
        .map((index) => Number(index))
        .sort((a, b) => a - b),
    [columns],
  );

  const cleanup = useEvent(() => {
    remove(pageAtom, { params: { appUid, pageId } });
    remove(['root', 'dataStore', appUid, pageId]);
    remove(['pageStore', 'runtime', 'cloudio', pageId]);
    DSAttributesCache.clear(appUid, ds);
    remove(attributesPath(appUid, ds));
    remove(pkAttributesPath(appUid, ds));
  });

  useEffect(() => {
    if (indexes.length === 0) return;
    platform.startAsyncTransition(async () => {
      const attrs = indexes.map((index) => {
        const c = columns[index];
        return {
          attributeCode: c.code,
          attributeType: c.dataType,
          allowBlank: 'Y',
          columnName: c.columnName,
          appUid,
          attributeName: c.title,
          selectAllowed: 'Y',
          insertAllowed: 'N',
          updateAllowed: 'N',
          queryAllowed: 'Y',
          primaryKey: 'N',
        } as Attribute;
      });
      set<Attribute[]>(attributesPath(appUid, ds), attrs);
      set<Attribute[]>(pkAttributesPath(appUid, ds), [
        {
          attributeCode: 'id',
          attributeType: 'String',
          allowBlank: 'N',
          primaryKey: 'Y',
          appUid,
        } as Attribute,
      ]);
      DSAttributesCache.setInitializedAttributes(appUid, ds);

      set(pageAtom, getPage(ds), { params: { appUid, pageId } });
      await store.clear();
      store.setBusy(false);
      store.setInitialQueryFired();
      store.processServerResponse(sampleRows);
      store.hasMore = false;
      await autoPopulateChildrenActionFn({
        appUid,
        platform,
        pageId,
        itemId: 'form',
        silent: true,
      });
      await autoPopulateChildrenActionFn({
        appUid,
        platform,
        pageId,
        itemId: 'canvasGrid',
        silent: true,
      });
    }, emptyFunction);
    return cleanup;
  }, [
    appUid,
    autoPopulateChildrenActionFn,
    columns,
    ds,
    indexes,
    pageId,
    platform,
    sampleRows,
    set,
    store,
  ]);

  const r = useComponentSize();
  const s = useSiblingComponentSize();
  const ss = JSON.parse(s.size);
  let w = r.width - 32;
  let h = r.height - 300;
  if (ss.length === 4) {
    w = ss[0][0];
    // root height - (divs other than preview's height) - gap - padding
    h = r.height - (ss[0][1] + ss[1][1] + ss[3][1]) - 16 * 3 - 32;
  }

  if (width < 700) {
    return (
      <NoDataFound
        color="error"
        msg={
          <>
            <Typography>
              DataSource Wizard requires atleast 700px width. You got only{' '}
              {width}
              px!
            </Typography>
            <ExpandCollapseIcon hideShareUrl />
          </>
        }
      />
    );
  }

  return (
    <div ref={r.ref} className={classes.root}>
      <div className={classes.fields}>
        <div className={classes.fieldsList}>
          {indexes.map((index) => {
            const c = columns[index];
            const { iconName, iconType } = parseIconText(
              FieldTypeIcons[c.dataType],
            );
            return (
              <Chip
                key={c.code}
                icon={
                  <Icon
                    iconName={iconName}
                    iconType={iconType}
                    parentColor={index === columnIndex ? 'primary' : undefined}
                  />
                }
                label={c.title}
                color={index === columnIndex ? 'primary' : undefined}
                onClick={() => {
                  setColumnIndex(index);
                }}
              />
            );
          })}
        </div>
        <div className={classes.columnEdit}>
          <TextField
            disabled={busy}
            variant="standard"
            fullWidth
            label="Title"
            value={columns[columnIndex].title ?? ''}
            onChange={(e) => {
              setColumns(
                setIn(columns, [columnIndex, 'title'], e.target.value),
              );
            }}
            inputProps={{
              maxLength: 60,
            }}
          />
          <TextField
            disabled={busy}
            variant="standard"
            fullWidth
            label="Code"
            value={columns[columnIndex].code ?? ''}
            onChange={(e) => {
              setColumns(
                setIn(
                  columns,
                  [columnIndex, 'code'],
                  camelCase(e.target.value),
                ),
              );
            }}
            inputProps={{
              maxLength: 60,
            }}
          />
          <TextField
            disabled={busy}
            variant="standard"
            fullWidth
            label="Column Name"
            value={columns[columnIndex].columnName ?? ''}
            onChange={(e) => {
              const columnName = e.target.value.endsWith(' ')
                ? e.target.value
                : snakeCase(e.target.value.toLowerCase()).toUpperCase();
              setColumns(
                setIn(columns, [columnIndex, 'columnName'], columnName),
              );
            }}
            inputProps={{
              maxLength: 60,
            }}
          />
          <FormControl variant="standard" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              disabled={busy}
              value={columns[columnIndex].dataType ?? ''}
              onChange={(e) => {
                setColumns(
                  setIn(
                    columns,
                    [columnIndex, 'dataType'],
                    e.target.value as FieldType,
                  ),
                );
              }}
            >
              {FieldTypeArray.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {columns[columnIndex].dataType === 'String' ? (
            <TextField
              disabled={busy}
              variant="standard"
              fullWidth
              label="Max Length"
              value={columns[columnIndex].maxLen ?? ''}
              onChange={(e) => {
                setColumns(
                  setIn(
                    columns,
                    [columnIndex, 'maxLen'],
                    Number(e.target.value),
                  ),
                );
              }}
              type="number"
            />
          ) : null}
          {columns[columnIndex].dataType === 'Date' ||
          columns[columnIndex].dataType === 'DateTime' ? (
            <TextField
              disabled={busy}
              variant="standard"
              fullWidth
              label="Date Format"
              value={
                columns[columnIndex].dateFormat ||
                (columns[columnIndex].dataType === 'Date' ? df : dtf)
              }
              onChange={(e) => {
                setColumns(
                  setIn(columns, [columnIndex, 'dateFormat'], e.target.value),
                );
              }}
              inputProps={{
                maxLength: 60,
              }}
              helperText={
                <Typography variant="caption">
                  Check this{' '}
                  <Button
                    sx={{ padding: 0, minWidth: 'unset' }}
                    rel="noreferrer"
                    target="_blank"
                    href="https://docs.rs/chrono/0.4.23/chrono/format/strftime/index.html"
                  >
                    link
                  </Button>{' '}
                  for more details on the format string
                </Typography>
              }
            />
          ) : null}
        </div>
      </div>
      <div className={classes.previewTitle}>
        <Typography variant="subtitle1">Preview</Typography>
      </div>
      <div ref={s.ref} className={classes.preview}>
        {indexes.length ? (
          <StaticRuntimeContext.Provider value>
            <PageContainer
              width={w}
              height={h}
              appUid={appUid}
              roleUid={roleUid}
              pageId={pageId}
              paramString=""
              embedded
            />
          </StaticRuntimeContext.Provider>
        ) : null}
      </div>
      <Box sx={{ display: 'flex', flexShrink: 0, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            back();
            setTimeout(() => {
              cleanup();
            }, 2000);
          }}
        >
          Back
        </Button>
        <Button
          size="medium"
          disabled={busy}
          fullWidth={false}
          variant="contained"
          startIcon={
            busy ? (
              <WaveDots active />
            ) : (
              <Icon iconName="cloud-upload" parentColor="primary" />
            )
          }
          onClick={async () => {
            setBusy(true);
            try {
              const response = await fetch(
                `${getUrl()}load/ds_${accept}?${getURLQueryStringToPost(user)}`,
                {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'X-Application': appUid,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    columns,
                    uri: result.uri,
                    datasourceUid: result.datasourceUid,
                    tableName: result.tableName,
                  }),
                },
              );
              const json = await response.json();
              if (json.status === 'ERROR') {
                platform.showError(json.message);
              } else {
                await listStore.refresh();
                setEditId(result.datasourceUid);
                setTitle('Choose the next step');
                setView('next');
                platform.showSuccess(
                  `${json.count} rows loaded into table ${result.tableName}.`,
                );
                setTimeout(() => {
                  cleanup();
                }, 2000);
              }
            } finally {
              setBusy(false);
            }
          }}
        >
          Submit
        </Button>
      </Box>
    </div>
  );
}

export default DataSourceWizardReview;
